import type { ContextEntry, ContextType } from './types';

/**
 * CONTEXT_TYPE_LABELS
 */
export const CONTEXT_TYPE_LABELS: Record<ContextType, string> = {
  url: 'URL',
  text: 'Text',
  github: 'GitHub',
  file: 'File',
  flow: 'FLOW:Context',
  thread: 'Thread',
  agent: 'Agent',
  mcp: 'MCP Server',
};

/**
 * entryKey
 * @param e contextEntry
 */
export function entryKey(e: ContextEntry): string {
  switch (e.type) {
    case 'github':
      return `github:${e.org}/${e.repo}/${e.path}`;
    case 'agent':
      return `agent:${e.url}`;
    case 'mcp':
      return `mcp:${e.url}`;
    case 'url':
      return `url:${e.value}`;
    case 'flow':
      return `flow:${e.org}/${e.poolId}`;
    case 'text':
      return `text:${e.value}`;
    case 'thread':
      return `thread:${e.threadTs}`;
    case 'file':
      return `file:${e.value}`;
    default:
      return '';
  }
}

/**
 * entryLabel
 * @param entry contextEntry
 */
export function entryLabel(entry: ContextEntry): string {
  if (entry.type === 'github')
    return `${entry.org}/${entry.repo}/${entry.path}`;
  if (entry.type === 'flow') return `${entry.org}/${entry.poolId}`;
  if (entry.type === 'thread') {
    const date = new Date(
      parseFloat(entry.threadTs) * 1000,
    ).toLocaleDateString();
    return `Saved conversation (${date})`;
  }
  if (entry.type === 'agent') return entry.url;
  if (entry.type === 'mcp') return entry.label ?? entry.url;
  return entry.value;
}

/**
 * Computes the Vault entryKey for a flow or agent context entry.
 * Mirrors the backend `entryKeyFor()` in vaultService.ts.
 * @param entry - Context entry to compute key for
 * @returns Vault entryKey
 */
export async function computeVaultEntryKey(
  entry:
    | { type: 'flow'; org: string; poolId: string }
    | { type: 'agent'; url: string }
    | { type: 'mcp'; url: string },
): Promise<string> {
  if (entry.type === 'flow') {
    const safe = (s: string) => s.replace(/[^a-zA-Z0-9_-]/g, '-');
    return `flow-${safe(entry.org)}-${safe(entry.poolId)}`;
  }
  const buf = await crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(entry.url),
  );
  const hex = Array.from(new Uint8Array(buf))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
  const prefix = entry.type === 'mcp' ? 'mcp' : 'agent';
  return `${prefix}-${hex.slice(0, 16)}`;
}

/**
 * entryChipColor
 * @param type url
 */
export function entryChipColor(type: ContextEntry['type']): string {
  if (type === 'url') return 'primary';
  if (type === 'text') return 'success';
  if (type === 'flow') return 'purpleBlue';
  if (type === 'github') return 'lightGray';
  if (type === 'file') return 'medGray';
  if (type === 'thread') return 'orange';
  if (type === 'agent') return 'pinkPurple';
  if (type === 'mcp') return 'teal';
  return 'medGray';
}
